import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TrendingUp, TrendingDown, Minus, Loader2 } from "lucide-react";
import { CopyableValue } from "@/components/clicks/CopyableValue";
import { useSpyserpDomainKeywords } from "@/hooks/useSpyserpDomainKeywords";
import { getSpyRankBadgeClass } from "@/lib/spy-display";

interface DomainKeywordsModalProps {
  isOpen: boolean;
  onClose: () => void;
  domain: string | null;
  rankLimit?: number;
  startDate?: string;
  endDate?: string;
}

export const DomainKeywordsModal = ({
  isOpen,
  onClose,
  domain,
  rankLimit,
  startDate,
  endDate,
}: DomainKeywordsModalProps) => {
  const { data: keywords = [], isPending } = useSpyserpDomainKeywords({
    isOpen,
    domain,
    rankLimit,
    startDate,
    endDate,
  });

  const renderRankBadge = (rank: number) => {
    if (!rank || rank > 100) {
      return (
        <Badge variant="outline" className="badge-muted font-normal">
          {">100"}
        </Badge>
      );
    }
    return <Badge className={getSpyRankBadgeClass(rank)}>#{rank}</Badge>;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-4xl max-h-[85vh] flex flex-col overflow-hidden min-h-0">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {rankLimit
              ? `Top ${rankLimit} keywords for domain: `
              : "Top keywords for domain: "}
            <span className="text-primary">{domain}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-x-auto overflow-y-auto border rounded-md mt-4">
          <Table className="min-w-[760px]">
            <TableHeader className="sticky top-0 bg-background z-10 shadow-sm">
              <TableRow>
                <TableHead>Keyword</TableHead>
                <TableHead>URL</TableHead>
                <TableHead className="text-center">Position</TableHead>
                <TableHead className="text-right">Trend</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isPending ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-32 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : keywords.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="h-32 text-center text-muted-foreground"
                  >
                    No data
                  </TableCell>
                </TableRow>
              ) : (
                keywords.map((kw) => (
                  <TableRow key={`${kw.page_url}-${kw.keyword}`}>
                    <TableCell className="font-medium">{kw.keyword}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      <CopyableValue value={kw.page_url} noTruncate />
                    </TableCell>
                    <TableCell className="text-center">
                      {renderRankBadge(kw.current_rank)}
                    </TableCell>
                    <TableCell className="text-right">
                      {kw.rank_change > 0 ? (
                        <span className="signal-text-positive flex items-center justify-end text-sm tabular-nums">
                          <TrendingUp className="mr-1 h-3 w-3" />+
                          {kw.rank_change}
                        </span>
                      ) : kw.rank_change < 0 ? (
                        <span className="signal-text-negative flex items-center justify-end text-sm tabular-nums">
                          <TrendingDown className="mr-1 h-3 w-3" />
                          {kw.rank_change}
                        </span>
                      ) : (
                        <span className="text-muted-foreground flex items-center justify-end text-sm">
                          <Minus className="w-3 h-3 mr-1" />
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="mt-4 flex justify-end">
          <Button
            variant="destructive"
            onClick={onClose}
            className="px-8 cursor-pointer"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
